const m = require('mithril');
const io = require('../../javascripts/libs/socketio');
const edit = require('../../javascripts/layout/components/edit');
const modal = require('../../javascripts/layout/components/modal');
const feedsAnimationSpeed = require('../equipment/components/diagram/equipmentTemplates/helpers/feedsAnimationSpeed');
const damparAngleScaleValue = require('../equipment/components/diagram/equipmentTemplates/helpers/damperScale');
const { renderStaticSvg } = require('../equipment/components/diagram/equipmentTemplates/helpers/staticSvgRenderer');

function normalizeFlag(value) {
  if (value === true || value === 1 || value === '1' || value === 'true') return 1;
  if (value === false || value === 0 || value === '0' || value === 'false' || value == null) return 0;
  return value;
}

function committedOverrideRuntimeUpdate(data) {
  if (!data || !Object.prototype.hasOwnProperty.call(data, 'overrideActive')) return null;
  if (data.overrideRuntimeCommandRid == null || String(data.overrideRuntimeCommandRid) === '') return null;

  const pointRid = data['@rid'] || data.rid;
  if (!pointRid) return null;
  return {
    rid: pointRid,
    overrideActive: data.overrideActive,
    overrideRuntimeCommandRid: data.overrideRuntimeCommandRid
  };
}

function confirmedOverrideValueUpdate(data) {
  const update = committedOverrideRuntimeUpdate(data);
  if (!update) return null;
  if (Object.prototype.hasOwnProperty.call(data, 'value')) {
    update.value = data.value;
  }
  return update;
}

const activeSubscriberInstances = new Set();

function resubscribeActivePoints() {
  // Socket.IO rooms belong to a single connection. When a browser reconnects,
  // restore every active point room immediately instead of waiting for each
  // twenty-second renewal interval.
  const pointRids = new Set();
  activeSubscriberInstances.forEach(instance => {
    instance.subscriptions.forEach((subscribers, pointRid) => {
      if (subscribers && subscribers.size > 0) pointRids.add(pointRid);
    });
  });
  pointRids.forEach(pointRid => io.emit('subscribe', pointRid));
}

io.on('connect', resubscribeActivePoints);

class pointSubscriber {
  constructor() {
    this.subscriptions = new Map(); // pointRid -> Set<ctrl>
    this.subscriptionIntervals = new Map(); // pointRid -> intervalId
    this.animationsMap = new Map(); // Store animations for points
    this.listeningForUpdates = false;
    this.pendingUpdates = new Map(); // pointRid -> latest data
    this.flushTimer = null;
    this.flushIntervalMs = 250;
    this.boundUpdateHandler = (data) => {
      if (!data) return;
      const pointRid = data['@rid'] || data.rid;
      if (!pointRid) return;
      const subscribers = this.subscriptions.get(pointRid);
      if (!subscribers || subscribers.size === 0) return;
      // Point updates are partial. A runtime-only Override event and a value
      // poll can arrive in the same redraw window, so merge them instead of
      // letting the later event erase fields from the earlier one.
      this.pendingUpdates.set(
        pointRid,
        Object.assign({}, this.pendingUpdates.get(pointRid) || {}, data)
      );
      if (this.flushTimer) return;
      this.flushTimer = setTimeout(() => {
        this.flushPendingUpdates();
      }, this.flushIntervalMs);
    };
  }

  flushPendingUpdates() {
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
    if (!this.pendingUpdates.size) return;
    const pending = this.pendingUpdates;
    this.pendingUpdates = new Map();
    pending.forEach((data, pointRid) => {
      const subscribers = this.subscriptions.get(pointRid);
      if (!subscribers || subscribers.size === 0) return;
      subscribers.forEach(ctrl => {
        if (ctrl && typeof ctrl.handlePointUpdate === 'function') {
          ctrl.handlePointUpdate(data);
        }
      });
    });
  }
  subscribe(ctrl, diagram) {
    
    if (!ctrl?.column?.in?.['@rid']) {
      console.error("Error: Missing column '@rid' in subscribe()");
      return;
    }
    
    const pointRid = ctrl.column.in['@rid'];
    activeSubscriberInstances.add(this);
    ctrl.pointRid = pointRid;
    let subscribers = this.subscriptions.get(pointRid);
    const isFirstSubscription = !subscribers;
    if (!subscribers) {
      subscribers = new Set();
      this.subscriptions.set(pointRid, subscribers);
    }
    if (subscribers.has(ctrl)) {
      return ctrl;
    }
    if (isFirstSubscription) {
      // Subscribe to the point
      io.emit('subscribe', pointRid);
      // Renew subscription every 20 seconds
      const intervalId = setInterval(() => io.emit('subscribe', pointRid), 20000);
      this.subscriptionIntervals.set(pointRid, intervalId);
    }

    // Define handler for point updates
    ctrl.handlePointUpdate = (data, updateOptions) => {
      if (data['@rid'] !== pointRid && data.rid !== pointRid) return;
      if (typeof ctrl.column.in === 'string') return;

      if (edit.vm.editing() && !(ctrl.options?.skipEditCheck)) return;
      const pointUpdateSuppressed = typeof modal.vm.isPointUpdateSuppressed === 'function' &&
        modal.vm.isPointUpdateSuppressed(pointRid);
      if (pointUpdateSuppressed && !ctrl.options?.keepPollingOnModalOpen) {
        // Keep ordinary live values frozen only for points owned by the active
        // command modal. Do not discard the one-time, command-backed Override
        // state event: it may be merged with a value poll in the redraw queue,
        // so apply only its runtime fields while the point remains suppressed.
        data = updateOptions && updateOptions.confirmedOverrideValue === true
          ? confirmedOverrideValueUpdate(data)
          : committedOverrideRuntimeUpdate(data);
        if (!data) return;
      }

      // Capture previous state for precise change detection
      const prevOverride = ctrl.column.in.overrideActive;
      const prevAlarm = ctrl.column.in.alarmStatus;
      const prevAlarmError = ctrl.column.in.alarmError;

      // Apply incoming values locally (no model commit yet)
      const hasValue = Object.prototype.hasOwnProperty.call(data, 'value');
      const hasTime = Object.prototype.hasOwnProperty.call(data, 'time');
      if (hasValue) ctrl.column.in.value = data.value;
      if (hasTime) ctrl.column.in.time = data.time;
      if (typeof data.overrideActive !== 'undefined') ctrl.column.in.overrideActive = data.overrideActive;
      if (typeof data.alarmStatus !== 'undefined') ctrl.column.in.alarmStatus = data.alarmStatus;
      if (typeof data.alarmError !== 'undefined') ctrl.column.in.alarmError = normalizeFlag(data.alarmError);


      const model = diagram?.model;
      if (!model || !model.modelData) {
        m.redraw();
        return; 
      }

      const valueKey = `${ctrl.column.in.equipmentClass == 'Feeds' ? ctrl.column.in.linkKey : ctrl.column.in.nodeKey}_${pointRid.toString()}`;
      const modelData = model.modelData;
      // Compare using normalized string, because we store String(value) in modelData
      const oldComparable = modelData[valueKey];
      const newComparable = String(data.value);
      const modelKeyMissing = typeof oldComparable === 'undefined';
      const valueChanged = hasValue && (modelKeyMissing || oldComparable !== newComparable);
      const overrideChanged = typeof data.overrideActive !== 'undefined' && data.overrideActive !== prevOverride;
      const alarmChanged = typeof data.alarmStatus !== 'undefined' && data.alarmStatus !== prevAlarm;
      const alarmErrorChanged = typeof data.alarmError !== 'undefined' && data.alarmError !== prevAlarmError;
      if (valueChanged) {
        switch (ctrl.column.in.equipmentClass) {
          case 'Damper':
            this.handleDamperAnimation(ctrl, diagram, data);
            break;
          case 'Fan':
            this.handleFanAnimation(ctrl, diagram, data);
            break;
          case 'StaticSvg':
            this.handleStaticSvg(ctrl, diagram, data);
            break;
          case 'Feeds':
            this.handleFeedsAnimation(ctrl, diagram, data);
            break;
          default:
            diagram.model.commit(function(mod) {
              mod.set(mod.modelData, `${ctrl.column['@rid'].toString()}_${ctrl.column.in['@rid'].toString()}`, String(ctrl.column.in.value));
            });
            break;
        }
      }
      // Handle override/alarm visual changes independently so both can run in one update
      if (overrideChanged || alarmChanged || alarmErrorChanged) {
        // Override/alarm toggled but numeric value unchanged — refresh bindings only
        const refreshedIn = Object.assign({}, ctrl.column.in);
        diagram.model.commit(function(mod) {
          mod.set(ctrl.column, 'in', refreshedIn);
        });
      }
    };

    // Register shared update listener once per instance
    if (!this.listeningForUpdates) {
      io.on('update', this.boundUpdateHandler);
      this.listeningForUpdates = true;
    }

    // Store subscription
    subscribers.add(ctrl);
    return ctrl;
  }

  handleDamperAnimation(ctrl, diagram, data) {
    let oldAngle = diagram.model.modelData[`${ctrl.column.in.nodeKey}_${ctrl.column.in['@rid'].toString()}_Angle`] || 0;
    diagram.model.commit(function (mod) {
      mod.set(mod.modelData, `${ctrl.column.in.nodeKey}_${ctrl.column.in['@rid'].toString()}`, String(ctrl.column.in.value));
    });

    const minValue = Number(ctrl.column.in.minValue || 0);
    const maxValue = Number(ctrl.column.in.maxValue || 100);
    const formula = ctrl.column.in.formula?.trim();
    const pointValues = (ctrl.column.in.pointsLinked || []).map(p => {
      return diagram.model.modelData[`${ctrl.column.in.nodeKey}_${p.rid}`];
    });
    
    const newAngle = damparAngleScaleValue(minValue, maxValue, pointValues, formula);
    const node = diagram.findNodeForKey(ctrl.column.in.nodeKey);
    
    if (node) {
      const damperLine = node.findObject(`DamperLine_${ctrl.column.in.nodeKey}`);
      if (damperLine) {
        const animation = new go.Animation();
        animation.easing = go.Animation.EaseInOutQuad;
        animation.duration = 1000;
        animation.add(damperLine, "angle", oldAngle, newAngle);
        animation.start();
        diagram.model.commit(function (mod) {
          mod.set(mod.modelData, `${ctrl.column.in.nodeKey}_${ctrl.column.in['@rid'].toString()}_Angle`, newAngle);
        });
      }
    }
  }

  handleFanAnimation(ctrl, diagram, data) {
    diagram.model.commit(function (mod) {
      mod.set(
        mod.modelData,
        `${ctrl.column.in.nodeKey}_${ctrl.column.in['@rid'].toString()}`,
        String(ctrl.column.in.value)
      );
    });
  
    const node = diagram.findNodeForKey(ctrl.column.in.nodeKey);
    if (!node) return;
  
    const strokeShape = node.findObject("animated-stroke");
    if (!strokeShape) return;
  
    const minValue = Number(ctrl.column.in.minValue || 0);
    const maxValue = Number(ctrl.column.in.maxValue || 100);
    const direction = ctrl.column.in.direction || 'clockwise';
    const formula = ctrl.column.in.formula?.trim();
    const pointValues = (ctrl.column.in.pointsLinked || []).map(p => {
      return diagram.model.modelData[`${ctrl.column.in.nodeKey}_${p.rid}`];
    });
  
    const animKey = ctrl.column.in.nodeKey;
    const fanSpeed = feedsAnimationSpeed(
      minValue,
      maxValue,
      formula,
      pointValues,
      strokeShape.strokeDashArray,
      0.5, // min speed scale
      0    // offset
    );
  
    const existing = this.animationsMap.get(animKey);
  
    if (ctrl.column.in.oldSpeed != fanSpeed) {
      ctrl.column.in.oldSpeed = fanSpeed;
      // If no animation and speed > 0, start one immediately
      if (!existing && fanSpeed > 0) {
        const animation = this.createFeedsAndFansAnimationFromOffset(strokeShape, direction, fanSpeed, 0);
        this.animationsMap.set(animKey, animation);
        animation.start();
        return;
      }
  
      // If animation exists and speed is 0 — stop and delete
      if (existing && fanSpeed === 0) {
        existing.stop();
        this.animationsMap.delete(animKey);
        return;
      }
  
      // If speed changed, defer restart
      if (existing && fanSpeed > 0 && existing.duration !== fanSpeed) {
        // Stop old animation after its current cycle 
        const offsetNow = strokeShape.strokeDashOffset || 0;
        const newAnim = this.createFeedsAndFansAnimationFromOffset(strokeShape, direction, fanSpeed, offsetNow);
        existing.stop();
        this.animationsMap.set(animKey, newAnim);
        newAnim.start();
      }
    }
  }

  handleStaticSvg(ctrl, diagram, data) {
    diagram.model.commit(function (mod) {
      mod.set(
        mod.modelData,
        `${ctrl.column.in.nodeKey}_${ctrl.column.in['@rid'].toString()}`,
        String(ctrl.column.in.value)
      );
    });

    const node = diagram.findNodeForKey(ctrl.column.in.nodeKey);
    if (!node) return;

    const pointValues = (ctrl.column.in.pointsLinked || []).map(pointLinked => {
      const key = `${ctrl.column.in.nodeKey}_${pointLinked.rid}`;
      const storedValue = diagram.model.modelData[key];
      const numeric = Number(storedValue);
      if (Number.isFinite(numeric)) return numeric;
      const fallback = Number(ctrl.column.in.value);
      return Number.isFinite(fallback) ? fallback : 0;
    });

    renderStaticSvg(node, {
      staticSvgs: ctrl.column.in.staticSvgs || [],
      formula: ctrl.column.in.formula,
      pointValues,
      equipmentName: ctrl.column.in.equipmentName,
      rid: ctrl.column.in.nodeKey
    });
  }

  handleFeedsAnimation(ctrl, diagram, data) {
    diagram.model.commit(function (mod) {
      mod.set(mod.modelData, `${ctrl.column.in.linkKey}_${ctrl.column.in['@rid'].toString()}`, String(ctrl.column.in.value));
    });
  
    let link;
    diagram.links.each(l => {
      if (l.data.key === ctrl.column.in.linkKey) link = l;
    });
    if (!link) return;
  
    const strokeShape = link.findObject("PIPE");
    if (!strokeShape) return;
  
    const minValue = Number(ctrl.column.in.minValue || 0);
    const maxValue = Number(ctrl.column.in.maxValue || 100);
    const direction = ctrl.column.in.direction || 'clockwise';
    const formula = ctrl.column.in.formula?.trim();
    const pointValues = (ctrl.column.in.pointsLinked || []).map(p => {
      return diagram.model.modelData[`${ctrl.column.in.linkKey}_${p.rid}`];
    });
  
    const linkSpeed = feedsAnimationSpeed(
      minValue,
      maxValue,
      formula,
      pointValues,
      strokeShape.strokeDashArray
    );
  
    const animKey = ctrl.column.in.linkKey;
    const existing = this.animationsMap.get(animKey);
  
    if (ctrl.column.in.oldSpeed != linkSpeed) {
      ctrl.column.in.oldSpeed = linkSpeed;
      // If there's no animation and speed > 0, start one immediately
      if (!existing && linkSpeed > 0) {
        const animation = this.createFeedsAndFansAnimationFromOffset(strokeShape, direction, linkSpeed, 0);
        this.animationsMap.set(animKey, animation);
        animation.start();
        return;
      }
  
      // If animation exists and speed is now 0 — stop and delete it
      if (existing && linkSpeed === 0) {
        existing.stop();
        this.animationsMap.delete(animKey);
        return;
      }
  
      // If speed changed, defer restart to preserve smoothness
      if (existing && linkSpeed > 0 && existing.duration !== linkSpeed) {
        // Stop old animation after its current cycle
        const offsetNow = strokeShape.strokeDashOffset || 0;
        const newAnim = this.createFeedsAndFansAnimationFromOffset(strokeShape, direction, linkSpeed, offsetNow);
        existing.stop();
        this.animationsMap.set(animKey, newAnim);
        newAnim.start();
        
      }
    }
  }

  unsubscribe(ctrl) {
    const pointRid = ctrl?.pointRid || ctrl?.column?.in?.['@rid'];
    if (!pointRid) {
      console.warn("Unsubscribe failed: Missing '@rid'");
      return;
    }

    const subscribers = this.subscriptions.get(pointRid);
    if (!subscribers) {
      if (ctrl && ctrl.handlePointUpdate) {
        ctrl.handlePointUpdate = null;
      }
      if (ctrl && ctrl.pointRid === pointRid) {
        ctrl.pointRid = null;
      }
      return;
    }
    subscribers.delete(ctrl);
    if (ctrl.handlePointUpdate) {
      ctrl.handlePointUpdate = null;
    }
    if (ctrl.pointRid === pointRid) {
      ctrl.pointRid = null;
    }

    if (subscribers.size === 0) {
      this.subscriptions.delete(pointRid);
      const intervalId = this.subscriptionIntervals.get(pointRid);
      if (intervalId) clearInterval(intervalId);
      this.subscriptionIntervals.delete(pointRid);
      io.emit('unsubscribe', pointRid);
    }

    const equipmentClass = ctrl?.column?.in?.equipmentClass;
    if (equipmentClass === 'Fan' || equipmentClass === 'Feeds') {
      const animKey = equipmentClass === 'Fan' ? ctrl?.column?.in?.nodeKey : ctrl?.column?.in?.linkKey;
      if (animKey && this.animationsMap.has(animKey)) {
        const animation = this.animationsMap.get(animKey);
        if (animation && typeof animation.stop === 'function') {
          animation.stop();
        }
        this.animationsMap.delete(animKey);
      }
    }

    if (this.subscriptions.size === 0 && this.listeningForUpdates) {
      io.off('update', this.boundUpdateHandler);
      this.listeningForUpdates = false;
      if (this.flushTimer) {
        clearTimeout(this.flushTimer);
        this.flushTimer = null;
      }
      this.pendingUpdates.clear();
      activeSubscriberInstances.delete(this);
    }
  }

  unsubscribeAll() {
    this.subscriptions.forEach((ctrlSet, pointRid) => {
      ctrlSet.forEach(ctrl => {
        if (ctrl && ctrl.handlePointUpdate) {
          ctrl.handlePointUpdate = null;
        }
      });
      const intervalId = this.subscriptionIntervals.get(pointRid);
      if (intervalId) clearInterval(intervalId);
      io.emit('unsubscribe', pointRid);
    });
    this.subscriptions.clear();
    this.subscriptionIntervals.clear();
    if (this.listeningForUpdates) {
      io.off('update', this.boundUpdateHandler);
      this.listeningForUpdates = false;
    }
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
    this.pendingUpdates.clear();
    this.animationsMap.forEach(animation => {
      if (animation && typeof animation.stop === 'function') {
        animation.stop();
      }
    });
    this.animationsMap.clear();
    activeSubscriberInstances.delete(this);
  }

  createFeedsAndFansAnimationFromOffset(strokeShape, direction, duration, currentOffset) {
    const dashLength = (strokeShape.strokeDashArray?.[0] || 10) + (strokeShape.strokeDashArray?.[1] || 0);
    const animation = new go.Animation();
    animation.easing = go.Animation.EaseLinear;
    animation.runCount = Infinity;
    animation.duration = duration;
  
    const strokeAnimationValue = strokeShape.strokeDashArray?.[0] * 2 || 10;
    const start = strokeShape.strokeDashOffset % dashLength
    const end = (strokeShape.strokeDashOffset % dashLength) + dashLength
    if (direction === 'counterclockwise') {
      animation.add(strokeShape, "strokeDashOffset", start, end);
    } else {
      animation.add(strokeShape, "strokeDashOffset", end, start);
    }
  
    return animation;
  }
};

pointSubscriber.publishConfirmedOverrideUpdate = function (data) {
  const pointRid = data && (data['@rid'] || data.rid);
  if (!pointRid) return;

  activeSubscriberInstances.forEach(instance => {
    const subscribers = instance.subscriptions.get(pointRid);
    if (!subscribers || subscribers.size === 0) return;

    // Apply any older queued poll first. The confirmed command value must be
    // the latest local state until a genuinely later poll arrives to verify or
    // correct it.
    instance.flushPendingUpdates();
    subscribers.forEach(ctrl => {
      if (ctrl && typeof ctrl.handlePointUpdate === 'function') {
        ctrl.handlePointUpdate(data, { confirmedOverrideValue: true });
      }
    });
  });
};

module.exports = pointSubscriber;
